// ---------------------------------------------------------------------------
// useRoadContribution — the single road-resolution gate every ad-hoc
// contribution passes through.
//
// R1 invariant: no new road contribution may be created from a passive raw GPS
// point. Rather than repeat that rule in TagButton, ContributeSheet and
// QuickReportButton — three chances to get it subtly different — every call
// site asks this hook for a road and gets one of exactly three answers:
//
//   resolved    — a real road, with a representative point derived from its
//                 geometry. Submit at THAT point, never at the fix.
//   unresolved  — no road data reachable (dead zone, outside coverage). The
//                 caller queues it with needsRoadResolution so it is held on
//                 the device and finished after reconnecting.
//   cancelled   — the user saw the picker and said "None of these". Nothing is
//                 saved. There is deliberately no fourth answer that means
//                 "use their location anyway".
//
// The picker element must be rendered by the caller: `const { requestRoad,
// picker } = useRoadContribution();` then `{picker}` somewhere in the tree.
// ---------------------------------------------------------------------------
import { useCallback, useRef, useState } from "react";
import { RoadPickerDialog } from "@/components/RoadPickerDialog";
import {
  resolveRoadAt, type AmbiguityReason, type RoadCandidate, type RoadMode,
} from "@/utils/roadResolve";
import type { LngLat } from "@/utils/polyline";

export interface ResolvedRoad {
  /** THE PUBLIC COORDINATE. Derived from the road's geometry. */
  representative: LngLat;
  /** `pl:` identity of the matched edge. */
  segmentRef: string;
  /** How far the contributor's point was from the road, metres. Honest
   *  provenance — for a confirmed road it may be far larger than 25. */
  distM: number;
  /** The road's name, when OSM has one. */
  roadName: string | null;
  /** Which of the two routes to identity this took. Decides the row's
   *  `source`, and therefore how the R0 trigger treats it. */
  via: "auto" | "user_confirmed";
}

export type RoadOutcome =
  | { status: "resolved"; road: ResolvedRoad }
  | { status: "unresolved"; reason: "offline" | "empty" | "error" }
  | { status: "cancelled" };

export interface RoadRequest {
  point: LngLat;
  mode: RoadMode;
  /** Reported accuracy of the fix, metres. null when the platform gave none —
   *  which FAILS the gate. Pass null with userPlacedPoint for a map tap. */
  accuracyM: number | null;
  /** The user pointed at this spot, so there is no fix quality to judge. */
  userPlacedPoint?: boolean;
}

const toRoad = (c: RoadCandidate, via: ResolvedRoad["via"]): ResolvedRoad => ({
  representative: c.representative,
  segmentRef: c.segmentRef,
  distM: c.distM,
  roadName: c.label,
  via,
});

export function useRoadContribution() {
  const [busy, setBusy] = useState(false);
  const [picker, setPicker] = useState<{
    candidates: RoadCandidate[];
    reason: AmbiguityReason;
  } | null>(null);
  // Resolves the promise `requestRoad` handed back, once the user answers.
  const answerRef = useRef<((o: RoadOutcome) => void) | null>(null);

  const requestRoad = useCallback(async (req: RoadRequest): Promise<RoadOutcome> => {
    setBusy(true);
    let res;
    try {
      res = await resolveRoadAt(req.point, req.mode, {
        accuracyM: req.accuracyM,
        userPlacedPoint: req.userPlacedPoint,
      });
    } finally {
      setBusy(false);
    }

    if (res.status === "confident") {
      return { status: "resolved", road: toRoad(res.chosen, "auto") };
    }
    if (res.status === "no_data") {
      return { status: "unresolved", reason: res.reason };
    }

    // Ambiguous — hand it to the user and wait for their answer. This is the
    // normal path in a city canyon or a car park, not an error.
    return await new Promise<RoadOutcome>((resolve) => {
      answerRef.current = resolve;
      setPicker({ candidates: res.candidates, reason: res.reason });
    });
  }, []);

  const finish = useCallback((outcome: RoadOutcome) => {
    setPicker(null);
    const answer = answerRef.current;
    answerRef.current = null;
    answer?.(outcome);
  }, []);

  const pickerEl = (
    <RoadPickerDialog
      open={picker !== null}
      candidates={picker?.candidates ?? []}
      reason={picker?.reason ?? "separation"}
      onConfirm={(c) => finish({ status: "resolved", road: toRoad(c, "user_confirmed") })}
      onCancel={() => finish({ status: "cancelled" })}
    />
  );

  return { requestRoad, picker: pickerEl, busy };
}

/** The `source` a resolved road should be filed under. Keeping this here means
 *  no call site has to remember which value unlocks the R0 identity trigger. */
// Generic in the fallback so the caller's own literal survives. Declaring
// `fallback: "manual" | "in_trip_prompt"` widened the return type to the whole
// union, which submitQuickReport rightly rejects — it accepts "manual" or
// "user_confirmed_road" and nothing else. Every call site passes a fallback
// explicitly, so there is no default to preserve.
export const sourceForRoad = <F extends "manual" | "in_trip_prompt">(
  road: ResolvedRoad,
  fallback: F,
): "user_confirmed_road" | F =>
  road.via === "user_confirmed" ? ("user_confirmed_road" as const) : fallback;
